/**
 * The live region every toast speaks through. Toasts announced together are written as one batch,
 * since a reader reads an assertive region from the top and a second write would cut the first
 * message off part-read.
 */
import { ANNOUNCER_ATTRIBUTE } from '@rover/kibble/utils/announcer';

import { ANNOUNCE_DELAY, ANNOUNCE_INTERVAL } from './Toast.common';

const HIDDEN_WRAPPER_STYLE =
  'position:absolute;width:1px;height:1px;margin:-1px;padding:0;border:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap';

export const TOAST_LIVE_REGION_TEST_ID = 'toast-live-region';

type Announcement = {
  message: string;
  isCancelled: boolean;
};

const createWrapper = (): HTMLElement | undefined => {
  // A server render, or a native bundle reaching this through a shared entry point.
  if (typeof document === 'undefined') return undefined;

  const node = document.createElement('div');
  node.setAttribute('data-testid', TOAST_LIVE_REGION_TEST_ID);
  node.setAttribute('style', HIDDEN_WRAPPER_STYLE);
  node.setAttribute(ANNOUNCER_ATTRIBUTE, '');
  document.body.appendChild(node);

  return node;
};

const wrapper = createWrapper();

/** Waiting for the next batch. */
let pending: Announcement[] = [];
/** In the region now, kept so a withdrawal can empty it. */
let spoken: Announcement[] = [];
let timeout: ReturnType<typeof setTimeout> | undefined;
/** From the moment a batch is scheduled until its hold has passed. */
let isBusy = false;
let region: HTMLElement | undefined;
let hasRegionSpoken = false;

/**
 * A node of its own per batch: a reader drops a message identical to the last one it read from a
 * given node, and emptying that node does not reset what it remembers. One that has yet to speak
 * is reused, so the first toast of a page load gets a region the reader has long since seen.
 */
const openRegion = (): void => {
  if (!wrapper || (region && !hasRegionSpoken)) return;

  region = document.createElement('div');
  // Assertive: a polite message waits for a pause the toast may not outlive. Not `role="alert"`,
  // which would leave a permanent alert on every page importing Toast.
  region.setAttribute('aria-live', 'assertive');
  region.setAttribute('aria-atomic', 'true');
  hasRegionSpoken = false;
  // Empty, and filled a tick later: VoiceOver ignores a region that arrives with its message in it.
  wrapper.replaceChildren(region);
};

openRegion();

const liveOnly = (batch: Announcement[]): Announcement[] =>
  batch.filter((announcement) => !announcement.isCancelled);

/** One element per message: whitespace in a single text node collapses, so a break would be lost. */
const fill = (messages: string[]): void => {
  if (!region) return;

  region.replaceChildren(
    ...messages.map((message) => {
      const line = document.createElement('div');
      line.textContent = message;

      return line;
    })
  );
  hasRegionSpoken = hasRegionSpoken || messages.length > 0;
};

const flush = (): void => {
  clearTimeout(timeout);
  isBusy = true;
  openRegion();
  spoken = [];

  timeout = setTimeout(() => {
    spoken = liveOnly(pending);
    pending = [];
    fill(spoken.map((announcement) => announcement.message));

    // Held, so an arrival right behind this batch cannot cut it off before a reader reaches it.
    timeout = setTimeout(() => {
      isBusy = false;
      if (pending.length > 0) flush();
    }, ANNOUNCE_INTERVAL);
  }, ANNOUNCE_DELAY);
};

/** Speaks `message` in the next batch. The returned callback withdraws it. */
const announceToast = (message: string): (() => void) => {
  if (!wrapper) return () => {};

  const announcement: Announcement = { message, isCancelled: false };
  pending.push(announcement);
  if (!isBusy) flush();

  return () => {
    if (announcement.isCancelled) return;
    announcement.isCancelled = true;

    // A bare `indexOf` would hand `splice` a -1 once the batch has gone out.
    const waiting = pending.indexOf(announcement);
    if (waiting !== -1) {
      pending.splice(waiting, 1);
      return;
    }

    // Only once its whole batch is withdrawn: emptying mid-read would cut off the others.
    if (spoken.includes(announcement) && liveOnly(spoken).length === 0) {
      fill([]);
      spoken = [];
      // Nothing is left to be read, so the hold has nothing to protect.
      clearTimeout(timeout);
      isBusy = false;
      if (pending.length > 0) flush();
    }
  };
};

export default announceToast;
